import {
	TextField,
	DialogTitle,
	DialogContent,
	Dialog,
	Button,
	DialogActions,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
} from '@mui/material';
import { FC, useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getShopAttributesForm, getShopAttributesFormIsLoad } from 'redux/reducers/shops/selectors';
import { mapShopFieldsRequest, updateShopAttributesRequest } from 'redux/reducers/shops/reducer';

// Function to find matching key in availableKeys based on label
const findMatchingKey = (label: string, availableKeys: string[]): string => {
	const normalizedLabel = label.toLowerCase().replace(/\s+/g, '');

	// First try exact match (case insensitive)
	const exactMatch = availableKeys.find((key) => key.toLowerCase() === normalizedLabel);
	if (exactMatch) return exactMatch;

	// Try partial matches
	const partialMatch = availableKeys.find(
		(key) =>
			key.toLowerCase().includes(normalizedLabel) || normalizedLabel.includes(key.toLowerCase()),
	);
	if (partialMatch) return partialMatch;

	// Try common variations
	const variations = [
		normalizedLabel,
		normalizedLabel.replace(/\s+/g, ''),
		normalizedLabel.replace(/\s+/g, '_'),
		normalizedLabel.replace(/\s+/g, '-'),
		normalizedLabel.toUpperCase(),
		normalizedLabel.replace(/\s+/g, '').toUpperCase(),
	];

	const variationMatch = variations.find((variation) =>
		availableKeys.find((key) => key.toLowerCase() === variation.toLowerCase()),
	);

	if (variationMatch) {
		const match = availableKeys.find((key) => key.toLowerCase() === variationMatch.toLowerCase());
		if (match) return match;
	}

	return '';
};

export const MapFields: FC<{
	open: boolean;
	onClose: () => void;
	setStep: (step: number) => void;
	shopId: number | null;
}> = ({ open, onClose, setStep, shopId }) => {
	const dispatch = useDispatch();

	const shopAttributesForm = useSelector(getShopAttributesForm);
	const shopAttributesFormLoading = useSelector(getShopAttributesFormIsLoad);

	const availableKeys = useMemo(() => shopAttributesForm?.keys || [], [shopAttributesForm?.keys]);

	const [hsCode, setHsCode] = useState('');
	const [hsCodeError, setHsCodeError] = useState<string | null>(null);
	const [isActDisclosedAgent, setIsActDisclosedAgent] = useState('');
	const [isActDisclosedAgentError, setIsActDisclosedAgentError] = useState<string | null>(null);
	const [isReducedVatTbe, setIsReducedVatTbe] = useState('');
	const [reducedVatForTbeError, setReducedVatForTbeError] = useState<string | null>(null);
	const [sellerRegistrationCountry, setSellerRegistrationCountry] = useState('');
	const [sellerRegistrationCountryError, setSellerRegistrationCountryError] = useState<
		string | null
	>(null);
	const [originatingCountry, setOriginatingCountry] = useState('');
	const [originatingCountryError, setOriginatingCountryError] = useState<string | null>(null);
	const [warehouseLocationCountry, setWarehouseLocationCountry] = useState('');
	const [warehouseLocationCountryError, setWarehouseLocationCountryError] = useState<string | null>(
		null,
	);

	// Auto-populate fields when availableKeys are loaded
	useEffect(() => {
		if (availableKeys.length > 0 && !shopAttributesFormLoading) {
			// Auto-populate fields with matching keys
			const hsCodeMatch = findMatchingKey('HsCode', availableKeys);
			if (hsCodeMatch && !hsCode) {
				setHsCode(hsCodeMatch);
			}

			const actDisclosedAgentMatch = findMatchingKey('Act as disclosed agent', availableKeys);
			if (actDisclosedAgentMatch && !isActDisclosedAgent) {
				setIsActDisclosedAgent(actDisclosedAgentMatch);
			}

			const reducedVatMatch = findMatchingKey('Reduced VAT for TBE', availableKeys);
			if (reducedVatMatch && !isReducedVatTbe) {
				setIsReducedVatTbe(reducedVatMatch);
			}

			const sellerCountryMatch = findMatchingKey('Seller registration country', availableKeys);
			if (sellerCountryMatch && !sellerRegistrationCountry) {
				setSellerRegistrationCountry(sellerCountryMatch);
			}

			const originatingCountryMatch = findMatchingKey('Originating country', availableKeys);
			if (originatingCountryMatch && !originatingCountry) {
				setOriginatingCountry(originatingCountryMatch);
			}

			const warehouseCountryMatch = findMatchingKey('Warehouse location country', availableKeys);
			if (warehouseCountryMatch && !warehouseLocationCountry) {
				setWarehouseLocationCountry(warehouseCountryMatch);
			}
		}
	}, [
		availableKeys,
		shopAttributesFormLoading,
		hsCode,
		isActDisclosedAgent,
		isReducedVatTbe,
		sellerRegistrationCountry,
		originatingCountry,
		warehouseLocationCountry,
	]);

	// Use useMemo to compute validation state to prevent re-renders
	const isFormValid = useMemo(() => {
		return !!(
			hsCode &&
			isActDisclosedAgent &&
			isReducedVatTbe &&
			sellerRegistrationCountry &&
			originatingCountry &&
			warehouseLocationCountry
		);
	}, [
		hsCode,
		isActDisclosedAgent,
		isReducedVatTbe,
		sellerRegistrationCountry,
		originatingCountry,
		warehouseLocationCountry,
	]);

	const validateForm = () => {
		setHsCodeError(null);
		setIsActDisclosedAgentError(null);
		setReducedVatForTbeError(null);
		setSellerRegistrationCountryError(null);
		setOriginatingCountryError(null);
		setWarehouseLocationCountryError(null);

		let validations = true;

		if (!hsCode) {
			setHsCodeError('HsCode is required');
			validations = false;
		}
		if (!isActDisclosedAgent) {
			setIsActDisclosedAgentError('Act as disclosed agent is required');
			validations = false;
		}
		if (!isReducedVatTbe) {
			setReducedVatForTbeError('Reduced VAT for TBE is required');
			validations = false;
		}
		if (!sellerRegistrationCountry) {
			setSellerRegistrationCountryError('Seller registration country is required');
			validations = false;
		}
		if (!originatingCountry) {
			setOriginatingCountryError('Originating country is required');
			validations = false;
		}
		if (!warehouseLocationCountry) {
			setWarehouseLocationCountryError('Warehouse location country is required');
			validations = false;
		}

		return validations;
	};

	const handleHsCodeChange = (value: string) => {
		setHsCode(value);
		setHsCodeError(null);
	};

	const handleActAsDisclosedAgentChange = (value: string) => {
		setIsActDisclosedAgent(value);
		setIsActDisclosedAgentError(null);
	};

	const handleReducedVatForTbeChange = (value: string) => {
		setIsReducedVatTbe(value);
		setReducedVatForTbeError(null);
	};

	const handleSellerRegistrationCountryChange = (value: string) => {
		setSellerRegistrationCountry(value);
		setSellerRegistrationCountryError(null);
	};

	const handleOriginatingCountryChange = (value: string) => {
		setOriginatingCountry(value);
		setOriginatingCountryError(null);
	};

	const handleWarehouseLocationCountryChange = (value: string) => {
		setWarehouseLocationCountry(value);
		setWarehouseLocationCountryError(null);
	};

	const eraseForm = () => {
		setHsCode('');
		setIsActDisclosedAgent('');
		setIsReducedVatTbe('');
		setSellerRegistrationCountry('');
		setOriginatingCountry('');
		setWarehouseLocationCountry('');
	};

	const handleMapFields = () => {
		if (!validateForm() || !shopId) return;
		dispatch(
			updateShopAttributesRequest({
				shopId,
				attributes: {
					hsCode,
					isActDisclosedAgent,
					isReducedVatTbe,
					sellerRegistrationCountry,
					originatingCountry,
					warehouseLocationCountry,
				},
			}),
		);
		eraseForm();
		onClose();
		setStep(1);
	};

	const handleCancel = () => {
		eraseForm();
		onClose();
		setStep(1);
	};

	return (
		<>
			<DialogTitle sx={{ fontWeight: 'bold', color: 'primary.main' }}>Map Fields</DialogTitle>
			<DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
				{shopAttributesFormLoading && (
					<div style={{ textAlign: 'center', padding: '20px' }}>Loading available fields...</div>
				)}
				{!shopAttributesFormLoading && availableKeys.length > 0 && (
					<>
						<FormControl fullWidth error={!!hsCodeError} sx={{ mt: 1 }}>
							<InputLabel>HsCode</InputLabel>
							<Select
								value={hsCode}
								label="HsCode"
								onChange={(e) => handleHsCodeChange(e.target.value)}
								required
							>
								{availableKeys.map((key) => (
									<MenuItem key={key} value={key}>
										{key}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<FormControl fullWidth error={!!isActDisclosedAgentError}>
							<InputLabel>Act as disclosed agent</InputLabel>
							<Select
								value={isActDisclosedAgent}
								label="Act as disclosed agent"
								onChange={(e) => handleActAsDisclosedAgentChange(e.target.value)}
								required
							>
								{availableKeys.map((key) => (
									<MenuItem key={key} value={key}>
										{key}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<FormControl fullWidth error={!!reducedVatForTbeError}>
							<InputLabel>Reduced VAT for TBE</InputLabel>
							<Select
								value={isReducedVatTbe}
								label="Reduced VAT for TBE"
								onChange={(e) => handleReducedVatForTbeChange(e.target.value)}
								required
							>
								{availableKeys.map((key) => (
									<MenuItem key={key} value={key}>
										{key}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<FormControl fullWidth error={!!sellerRegistrationCountryError}>
							<InputLabel>Seller registration country</InputLabel>
							<Select
								value={sellerRegistrationCountry}
								label="Seller registration country"
								onChange={(e) => handleSellerRegistrationCountryChange(e.target.value)}
								required
							>
								{availableKeys.map((key) => (
									<MenuItem key={key} value={key}>
										{key}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<FormControl fullWidth error={!!originatingCountryError}>
							<InputLabel>Originating country</InputLabel>
							<Select
								value={originatingCountry}
								label="Originating country"
								onChange={(e) => handleOriginatingCountryChange(e.target.value)}
								required
							>
								{availableKeys.map((key) => (
									<MenuItem key={key} value={key}>
										{key}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<FormControl fullWidth error={!!warehouseLocationCountryError}>
							<InputLabel>Warehouse location country</InputLabel>
							<Select
								value={warehouseLocationCountry}
								label="Warehouse location country"
								onChange={(e) => handleWarehouseLocationCountryChange(e.target.value)}
								required
							>
								{availableKeys.map((key) => (
									<MenuItem key={key} value={key}>
										{key}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</>
				)}
				{!shopAttributesFormLoading && availableKeys.length === 0 && (
					<div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
						No available fields found. Please try again later.
					</div>
				)}
			</DialogContent>
			<DialogActions>
				<Button size="small" variant="outlined" color="primary" onClick={handleCancel}>
					Cancel
				</Button>
				<Button
					size="small"
					variant="contained"
					color="primary"
					onClick={handleMapFields}
					disabled={shopAttributesFormLoading || !isFormValid || !shopId}
				>
					Map Fields
				</Button>
			</DialogActions>
		</>
	);
};
